import type { FormEvent } from "react";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Dismiss20Regular,
  Save20Regular,
  ChevronDown20Regular,
} from "@fluentui/react-icons";
import { PageHeader } from "../../components/PageHeader";
import { AvatarUploader } from "../../components/AvatarUploader";
import {
  ApiError,
  getSubcontractorById,
  getSubcontractorSpecialties,
  updateSubcontractor,
  uploadSubcontractorLogo,
  type Subcontractor,
  type SubcontractorPayload,
  type SubcontractorSpecialty,
} from "../../subempreiteiros/subempreiteirosApi";
import { APP_LOCALE } from "../../config/locale";
import { EntityDocumentsSection } from "../../components/EntityDocumentsSection";

const CONTRACT_DOCUMENT_CATEGORIES = [
  "Empreitada",
  "Subempreitada",
  "Fornecimento",
  "Arrendamento",
  "Prestação de serviços",
  "Outro",
];

type EstadoSubempreiteiro = "Ativo" | "Inativo";

const parseErrorMessage = (error: unknown): string => {
  if (error instanceof ApiError) {
    if (error.status === 401) {
      return "Sessão expirada. Por favor, autentique-se novamente.";
    }
    if (error.status === 403) {
      return "Sem permissões para editar sub-empreiteiros.";
    }
  }

  if (error instanceof Error && error.message) {
    return error.message;
  }

  return "Não foi possível atualizar o sub-empreiteiro.";
};

const parseLoadError = (error: unknown): string => {
  if (error instanceof ApiError) {
    if (error.status === 401) {
      return "Sessão expirada. Por favor, autentique-se novamente.";
    }
    if (error.status === 403) {
      return "Sem permissões para consultar sub-empreiteiros.";
    }
  }

  if (error instanceof Error && error.message) {
    return error.message;
  }

  return "Não foi possível carregar o sub-empreiteiro.";
};

const parseSpecialtiesError = (error: unknown): string => {
  if (error instanceof ApiError) {
    if (error.status === 401) {
      return "Sessão expirada. Por favor, autentique-se novamente.";
    }
    if (error.status === 403) {
      return "Sem permissões para consultar especialidades.";
    }
  }

  return "Não foi possível carregar as especialidades.";
};

const formatDate = (value?: string | null): string => {
  if (!value) return "—";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "—";
  return parsed.toLocaleDateString(APP_LOCALE);
};

export function EditarSubempreiteiroView() {
  const navigate = useNavigate();
  const { subcontractorId } = useParams();
  const [subcontractorSnapshot, setSubcontractorSnapshot] =
    useState<Subcontractor | null>(null);
  const [nome, setNome] = useState("");
  const [especialidadesDisponiveis, setEspecialidadesDisponiveis] =
    useState<SubcontractorSpecialty[]>([]);
  const [especialidadesSelecionadas, setEspecialidadesSelecionadas] =
    useState<number[]>([]);
  const [especialidadesErro, setEspecialidadesErro] = useState<
    string | null
  >(null);
  const [estado, setEstado] =
    useState<EstadoSubempreiteiro>("Ativo");
  const [pessoaContacto, setPessoaContacto] = useState("");
  const [nif, setNif] = useState("");
  const [email, setEmail] = useState("");
  const [telefone, setTelefone] = useState("");
  const [logoUrl, setLogoUrl] = useState<string | null>(
    null,
  );
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(
    null,
  );
  const [submitError, setSubmitError] = useState<string | null>(
    null,
  );
  const [lastUpdatedLabel, setLastUpdatedLabel] = useState<
    string | null
  >(null);

  useEffect(() => {
    let isMounted = true;

    const loadSpecialties = async () => {
      setEspecialidadesErro(null);
      try {
        const response = await getSubcontractorSpecialties();
        if (!isMounted) return;
        setEspecialidadesDisponiveis(response);
      } catch (error) {
        if (!isMounted) return;
        setEspecialidadesErro(parseSpecialtiesError(error));
      }
    };

    loadSpecialties();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    const loadSubcontractor = async () => {
      if (!subcontractorId) {
        setLoadError("Sub-empreiteiro inválido.");
        setIsLoading(false);
        return;
      }

      const parsedId = Number(subcontractorId);
      if (Number.isNaN(parsedId)) {
        setLoadError("Sub-empreiteiro inválido.");
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setLoadError(null);
      setSubmitError(null);

      try {
        const response = await getSubcontractorById(parsedId);
        if (!isMounted) return;

        setSubcontractorSnapshot(response);
        setNome(response.name ?? "");
        setEspecialidadesSelecionadas(
          response.specialties?.map((specialty) => specialty.id) ??
            [],
        );
        setEstado(
          (response.status as EstadoSubempreiteiro) ?? "Ativo",
        );
        setPessoaContacto(response.contactName ?? "");
        setNif(response.taxId ?? "");
        setEmail(response.email ?? "");
        setTelefone(response.phoneNumber ?? "");
        setLogoUrl(response.logoUrl ?? null);
        setLastUpdatedLabel(formatDate(response.lastModified));
      } catch (error) {
        if (!isMounted) return;
        setLoadError(parseLoadError(error));
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadSubcontractor();

    return () => {
      isMounted = false;
    };
  }, [subcontractorId]);

  const toggleEspecialidade = (especialidadeId: number) => {
    if (especialidadesSelecionadas.includes(especialidadeId)) {
      setEspecialidadesSelecionadas(
        especialidadesSelecionadas.filter(
          (id) => id !== especialidadeId,
        ),
      );
    } else {
      setEspecialidadesSelecionadas([
        ...especialidadesSelecionadas,
        especialidadeId,
      ]);
    }
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setSubmitError(null);

    if (!subcontractorSnapshot) {
      setSubmitError("Sub-empreiteiro inválido.");
      return;
    }

    if (!nome.trim()) {
      setSubmitError("O nome do sub-empreiteiro é obrigatório.");
      return;
    }

    if (especialidadesSelecionadas.length === 0) {
      setSubmitError(
        "A especialidade do sub-empreiteiro é obrigatória.",
      );
      return;
    }

    setIsSaving(true);

    const payload: SubcontractorPayload = {
      name: nome.trim(),
      taxId: nif.trim() || null,
      specialtyIds: especialidadesSelecionadas,
      status: estado,
      contactName: pessoaContacto.trim() || null,
      email: email.trim() || null,
      phoneNumber: telefone.trim() || null,
      notes: subcontractorSnapshot.notes ?? null,
      projectsCount: subcontractorSnapshot.projectsCount,
    };

    try {
      await updateSubcontractor(subcontractorSnapshot.id, payload);
    } catch (error) {
      setSubmitError(parseErrorMessage(error));
      setIsSaving(false);
      return;
    }

    if (logoFile) {
      try {
        await uploadSubcontractorLogo(
          subcontractorSnapshot.id,
          logoFile,
        );
      } catch {
        setSubmitError(
          "Sub-empreiteiro atualizado, mas não foi possível guardar o logotipo.",
        );
        setIsSaving(false);
        return;
      }
    }

    setIsSaving(false);
    navigate("/subempreiteiros", {
      state: { toast: "Sub-empreiteiro atualizado com sucesso" },
    });
  };

  const actions = (
    <div className="flex items-center gap-4">
      <button
        className="px-4 py-2 bg-secondary hover:bg-accent border border-border rounded transition-colors flex items-center gap-2 min-h-[44px]"
        style={{ borderRadius: "var(--radius-button)" }}
        onClick={() => navigate("/subempreiteiros")}
        disabled={isSaving}
      >
        <Dismiss20Regular className="w-5 h-5" />
        <span className="hidden sm:inline">Cancelar</span>
      </button>
      <button
        className="px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded transition-colors flex items-center gap-2 min-h-[44px]"
        style={{
          borderRadius: "var(--radius-button)",
          height: "var(--button-height)",
        }}
        onClick={() => {
          const form = document.getElementById(
            "editar-subempreiteiro-form",
          ) as HTMLFormElement | null;
          form?.requestSubmit();
        }}
        disabled={isSaving || isLoading}
      >
        <Save20Regular className="w-5 h-5" />
        {isSaving ? "A guardar..." : "Guardar Alterações"}
      </button>
    </div>
  );

  return (
    <div className="flex flex-col h-full">
      <PageHeader
        title="Editar Sub-empreiteiro"
        subtitle="Atualize os dados do sub-empreiteiro e informações de contacto."
        actions={actions}
        backButton={{ onClick: () => navigate("/subempreiteiros") }}
      />

      <div className="flex-1 overflow-y-auto bg-background">
        <div className="p-4 md:p-6">
          {loadError && (
            <div
              className="bg-card border border-[var(--destructive)] px-4 py-3 mb-6 text-[var(--destructive)]"
              style={{ borderRadius: "var(--radius-card)" }}
            >
              {loadError}
            </div>
          )}

          {submitError && (
            <div
              className="bg-card border border-[var(--destructive)] px-4 py-3 mb-6 text-[var(--destructive)]"
              style={{ borderRadius: "var(--radius-card)" }}
            >
              {submitError}
            </div>
          )}

          <div
            className="text-secondary-text mb-6"
            style={{
              fontSize: "var(--text-caption)",
              fontWeight: "var(--font-weight-normal)",
            }}
          >
            Última atualização: {lastUpdatedLabel ?? "—"}
          </div>

          <form
            id="editar-subempreiteiro-form"
            onSubmit={handleSubmit}
          >
            <div className="max-w-3xl mx-auto space-y-6 mb-20">
              <div
                className="bg-card border border-border p-6 space-y-5"
                style={{ borderRadius: "var(--radius-card)" }}
              >
                <h3
                  className="text-foreground"
                  style={{
                    fontSize: "var(--text-section-title)",
                    fontWeight: "var(--font-weight-semi-bold)",
                  }}
                >
                  Informação Principal
                </h3>

                <div>
                  <label
                    className="block text-foreground mb-2"
                    style={{
                      fontSize: "var(--text-caption)",
                      fontWeight: "var(--font-weight-semi-bold)",
                    }}
                  >
                    Logotipo do Sub-empreiteiro
                  </label>
                  <AvatarUploader
                    currentImage={logoUrl}
                    onImageChange={setLogoFile}
                    variant="entity"
                    label=""
                    subtitle="Formatos suportados: JPG, PNG. Tamanho recomendado: 200×200."
                  />
                </div>

                <div>
                  <label
                    htmlFor="nomeSubempreiteiro"
                    className="block text-foreground mb-2"
                    style={{
                      fontSize: "var(--text-caption)",
                      fontWeight: "var(--font-weight-semi-bold)",
                    }}
                  >
                    Nome do Sub-empreiteiro{" "}
                    <span className="text-primary">*</span>
                  </label>
                  <input
                    id="nomeSubempreiteiro"
                    type="text"
                    value={nome}
                    onChange={(e) => setNome(e.target.value)}
                    placeholder="Insira o nome do sub-empreiteiro"
                    className="w-full px-3 py-2 border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                    style={{
                      borderRadius: "var(--radius-button)",
                      fontSize: "var(--text-base)",
                    }}
                    disabled={isLoading || isSaving}
                  />
                </div>

                <div>
                  <label
                    className="block text-foreground mb-2"
                    style={{
                      fontSize: "var(--text-caption)",
                      fontWeight: "var(--font-weight-semi-bold)",
                    }}
                  >
                    Especialidades{" "}
                    <span className="text-primary">*</span>
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {especialidadesDisponiveis.map(
                      (especialidade) => (
                        <button
                          key={especialidade.id}
                          type="button"
                          onClick={() =>
                            toggleEspecialidade(
                              especialidade.id,
                            )
                          }
                          className={`px-3 py-1.5 rounded transition-colors ${
                            especialidadesSelecionadas.includes(
                              especialidade.id,
                            )
                              ? "bg-primary text-primary-foreground"
                              : "bg-secondary hover:bg-accent border border-border text-foreground"
                          }`}
                          style={{
                            borderRadius: "var(--radius)",
                            fontSize: "var(--text-caption)",
                            fontWeight:
                              especialidadesSelecionadas.includes(
                                especialidade.id,
                              )
                                ? "var(--font-weight-semi-bold)"
                                : "var(--font-weight-normal)",
                          }}
                          disabled={isLoading || isSaving}
                        >
                          {especialidade.name}
                        </button>
                      ),
                    )}
                  </div>
                  {especialidadesErro && (
                    <p className="mt-2 text-sm text-destructive">
                      {especialidadesErro}
                    </p>
                  )}
                </div>

                <div>
                  <label
                    htmlFor="estado"
                    className="block text-foreground mb-2"
                    style={{
                      fontSize: "var(--text-caption)",
                      fontWeight: "var(--font-weight-semi-bold)",
                    }}
                  >
                    Estado
                  </label>
                  <div className="relative">
                    <select
                      id="estado"
                      value={estado}
                      onChange={(e) =>
                        setEstado(
                          e.target.value as EstadoSubempreiteiro,
                        )
                      }
                      className="w-full px-3 py-2 border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary appearance-none pr-10"
                      style={{
                        borderRadius: "var(--radius-button)",
                        fontSize: "var(--text-base)",
                      }}
                      disabled={isLoading || isSaving}
                    >
                      <option value="Ativo">Ativo</option>
                      <option value="Inativo">Inativo</option>
                    </select>
                    <ChevronDown20Regular className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-secondary-text pointer-events-none" />
                  </div>
                </div>
              </div>

              <div
                className="bg-card border border-border p-6 space-y-5"
                style={{ borderRadius: "var(--radius-card)" }}
              >
                <h3
                  className="text-foreground"
                  style={{
                    fontSize: "var(--text-section-title)",
                    fontWeight: "var(--font-weight-semi-bold)",
                  }}
                >
                  Contacto
                </h3>

                <div>
                  <label
                    htmlFor="pessoaContacto"
                    className="block text-foreground mb-2"
                    style={{
                      fontSize: "var(--text-caption)",
                      fontWeight: "var(--font-weight-semi-bold)",
                    }}
                  >
                    Pessoa de contacto
                  </label>
                  <input
                    id="pessoaContacto"
                    type="text"
                    value={pessoaContacto}
                    onChange={(e) =>
                      setPessoaContacto(e.target.value)
                    }
                    placeholder="Nome da pessoa de contacto"
                    className="w-full px-3 py-2 border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                    style={{
                      borderRadius: "var(--radius-button)",
                      fontSize: "var(--text-base)",
                    }}
                    disabled={isLoading || isSaving}
                  />
                </div>

                <div>
                  <label
                    htmlFor="nif"
                    className="block text-foreground mb-2"
                    style={{
                      fontSize: "var(--text-caption)",
                      fontWeight: "var(--font-weight-semi-bold)",
                    }}
                  >
                    NIF
                  </label>
                  <input
                    id="nif"
                    type="text"
                    value={nif}
                    onChange={(e) => setNif(e.target.value)}
                    placeholder="Número de Identificação Fiscal"
                    className="w-full px-3 py-2 border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                    style={{
                      borderRadius: "var(--radius-button)",
                      fontSize: "var(--text-base)",
                    }}
                    disabled={isLoading || isSaving}
                  />
                </div>

                <div>
                  <label
                    htmlFor="email"
                    className="block text-foreground mb-2"
                    style={{
                      fontSize: "var(--text-caption)",
                      fontWeight: "var(--font-weight-semi-bold)",
                    }}
                  >
                    Email
                  </label>
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="contacto@email.pt"
                    className="w-full px-3 py-2 border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                    style={{
                      borderRadius: "var(--radius-button)",
                      fontSize: "var(--text-base)",
                    }}
                    disabled={isLoading || isSaving}
                  />
                </div>

                <div>
                  <label
                    htmlFor="telefone"
                    className="block text-foreground mb-2"
                    style={{
                      fontSize: "var(--text-caption)",
                      fontWeight: "var(--font-weight-semi-bold)",
                    }}
                  >
                    Telefone
                  </label>
                  <input
                    id="telefone"
                    type="tel"
                    value={telefone}
                    onChange={(e) => setTelefone(e.target.value)}
                    placeholder="+351 912 345 678"
                    className="w-full px-3 py-2 border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                    style={{
                      borderRadius: "var(--radius-button)",
                      fontSize: "var(--text-base)",
                    }}
                    disabled={isLoading || isSaving}
                  />
                </div>
              </div>
            </div>
          </form>

          {subcontractorSnapshot && (
            <div className="mt-6">
              <EntityDocumentsSection
                ownerType="subcontractor"
                ownerId={subcontractorSnapshot.id}
                section="contract"
                title="Contratos e Documentos"
                variant="card"
                showCounterparty
                categories={CONTRACT_DOCUMENT_CATEGORIES}
                emptyLabel="Sem contratos ou documentos arquivados."
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
